import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

/**
 * Pricing-only placeholders (flag `placeholder_link_only`). The routes are
 * `requireFlag`-gated on the server; callers render only while the flag is on.
 */

export interface StorePlaceholder {
  id: string;
  name: string;
  brand: string;
  model: string;
  sku: string | null;
  upc: string | null;
  origin: 'platform_sheet' | 'private_sheet' | 'store_listing';
  selected: boolean;
}

export interface CreatePlaceholderInput {
  brand: string;
  title: string;
  sku?: string | null;
  barcode?: string | null;
  /** Naming a Shopify listing links the placeholder to it. */
  integrationId?: string | null;
  shopifyProductId?: string | null;
}

const failure = async (response: Response, fallback: string): Promise<never> => {
  let message = fallback;
  try {
    const body = await response.json();
    message = body?.message || body?.error || fallback;
  } catch {
    // keep the fallback
  }
  throw new Error(message);
};

export const placeholderApi = {
  async list(options: { search?: string; integrationId?: string | null } = {}): Promise<StorePlaceholder[]> {
    const params = new URLSearchParams();
    if (options.search?.trim()) params.set('search', options.search.trim());
    if (options.integrationId) params.set('integrationId', options.integrationId);
    const query = params.toString();
    const response = await makeApiCall(`/store/placeholders${query ? `?${query}` : ''}`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) return failure(response, 'Failed to load pricing-only placeholders');
    return (await response.json()).data || [];
  },

  /** Adds placeholders to the store's (or one integration's) sync selection. */
  async select(productIds: string[], integrationId?: string | null): Promise<{ added: string[]; skipped: string[] }> {
    const response = await makeApiCall('/store/placeholders/select', {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(integrationId ? { productIds, integrationId } : { productIds }),
    });
    if (!response.ok) return failure(response, 'Failed to add placeholders to the sync selection');
    return (await response.json()).data;
  },

  async createFromListing(input: CreatePlaceholderInput): Promise<{ productId: string; created: boolean }> {
    const response = await makeApiCall('/store/placeholders/from-listing', {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        integrationId: input.integrationId || null,
        shopifyProductId: input.shopifyProductId || null,
        title: input.title,
        brand: input.brand,
        sku: input.sku || null,
        barcode: input.barcode || null,
      }),
    });
    if (!response.ok) return failure(response, 'Failed to create a pricing-only placeholder');
    return (await response.json()).data;
  },
};
